import type { CartChild, CartChildDTO } from './child.ts';
import type { CommitmentFrequencyEnum } from './commitments.ts';
import type { CartItemFund } from './fund.ts';
import type { CartItemGift } from './gift.ts';
import type { CartItemPayment } from './payment.ts';
import type {
  CartItemSponsorship,
  CartItemSponsorshipDTO,
} from './sponsorship.ts';

/** All possible relationships a cart can have */
export enum CartItemRelEnum {
  AddOn = 'AddOn',
}

export enum CartItemTypeEnum {
  Fund = 'Fund',
  Sponsorship = 'Sponsorship',
  Gift = 'Gift',
  Payment = 'Payment',
}

export enum CartItemLegacyGroupEnum {
  ChildSelected = 'ChildSelected',
  LongestWaiting = 'LongestWaiting',
  EPayment = 'EPayment',
  FirstPayment = 'FirstPayment',
  ChildCriteria = 'ChildCriteria',
  Fund = 'Fund',
  CommitmentPayment = 'CommitmentPayment',
  GiftPayment = 'GiftPayment',
  Premium = 'Premium',
}

export type CartItemTypeData =
  | CartItemFund
  | CartItemSponsorship
  | CartItemGift
  | CartItemPayment;

/**
 * Shared properties across all cart item types.
 */
export interface CartItemBase {
  /**
   * ID used for PATCH/DELETE calls.
   */
  id: string;
  /**
   * The item code.
   * */
  itemCode: string;
  /**
   *  Timestamp (ISO in ms) when the item was added to the cart.
   *  Can be used to determine a recently added item.
   */
  added: string;
  /**
   * Optional. If this item is dependent on another item (and would be removed when that item is removed).
   */
  parentId?: string;
  /**
   * Optional. Relationship, in order to display or not display it as separate item or
   * as a dependent */
  rel?: CartItemRelEnum;
  /**
   * Optional. If this is a parent item with dependent items, the parent includes the ids of all of the children (to cascade deletes)
   */
  dependentIds?: string[];
  /**
   * Required for fund or gift (maybe move).
   */
  commitmentFrequency?: CommitmentFrequencyEnum;
  /**
   * Rate of the item.
   */
  unitPrice: number;
  /**
   * Quantity of item, usually only used for certain sponsorship types.
   */
  quantity?: number;
  /**
   * unitPrice of the item multiplied by the quantity
   */
  amount: number;
  /**
   * Optional. Allows for gifts to be given next year - pulled at the appropriate time
   */
  excludeFromToday?: boolean;
  /**
   * Image URL.
   */
  thumb?: string;
  /**
   * Contains the child details for a sponsorship, gift, or payment
   */
  child?: CartChild;
  /**
   * This item was an upsell item added to the cart
   */
  isUpsell?: boolean;
  /**
   * When true, indicates the user explicitly selected this rate (e.g. via dual-rate options).
   * Prevents campaign subsidy logic from overwriting the user's chosen unitPrice.
   */
  hasUserSelectedRate?: boolean;
}

/**
 * A single cart item including properties
 * belonging to all product/item variations.
 *
 * @discriminator itemType
 */
export type CartItem = CartItemBase & CartItemTypeData;

interface CartItemBaseResponseDTO extends Omit<CartItemBase, 'child'> {
  child?: CartChildDTO;
}

type CartItemTypeDataDTO =
  | CartItemFund
  | CartItemSponsorshipDTO
  | CartItemGift
  | CartItemPayment;

export type CartItemDTO = CartItemBaseResponseDTO & CartItemTypeDataDTO;
